import { FC } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useNavigate } from "react-router";
import { colors } from "../../styles/colors";
import { Icon } from "../Icon/Icon";

/**
 * "Véhicule éligible pour la géolocalisation et l'éco-conduite" — and the four
 * other things that sentence could have been.
 *
 * WHY THIS IS NOT A SIMPLE YES/NO CHIP
 * ------------------------------------
 * The obvious build is `{vehicle.lbc_eligibilty && <Chip>Éligible</Chip>}`.
 * That is wrong, because the underlying column is tri-state (1 / 0 / NULL) and
 * the API used to flatten it with a boolean cast. A vehicle nobody has checked
 * yet and a vehicle the provider has actively rejected both arrived as `false`,
 * so a boolean chip silently tells a customer their new car is incompatible
 * when the truth is that we never asked.
 *
 * Since telematics became plan-gated, NULL is also the permanent state of every
 * vehicle on an Essential plan. Showing "not eligible" there would be the worst
 * version of the message: it blames the car for a commercial boundary. The car
 * may be perfectly capable — the offer is what is missing.
 *
 * So the badge renders the server's `telematics.status`, and each state gets
 * the ONE next step that actually unblocks the user.
 *
 * DENSITY, AND NOT NAGGING
 * ------------------------
 * `plan_excluded` is a fleet-wide truth: it is identical on all 500 rows of the
 * vehicle table. Repeating an upgrade prompt 500 times is nagging, not
 * informing, and it trains people to ignore the column. So in `compact` density
 * (list rows) that state renders nothing at all, and the upsell appears only in
 * `full` density (the vehicle detail page) where the user is actually looking
 * at one vehicle and can act.
 *
 * ACCESSIBILITY
 * -------------
 * Never colour alone: every state carries an icon AND a text label, and the
 * compact form keeps the full sentence in `title` + `aria-label` so the meaning
 * survives both a screen reader and a monochrome display.
 */

export type TelematicsStatus =
  | "eligible"
  | "not_eligible"
  | "pending"
  | "plan_excluded"
  | "no_vin";

export type TelematicsInfo = {
  status: TelematicsStatus;
  /** Raw provider answer: null = never asked. */
  provider_flag: boolean | null;
  /** True only when the plan is the sole blocker AND the vehicle is confirmed compatible. */
  upgrade_unlocks: boolean;
};

const useStyles = createUseStyles({
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.3,
    padding: "3px 10px",
    borderRadius: "999px",
    border: "1px solid transparent",
    whiteSpace: "nowrap" as const,
    maxWidth: "100%",
  },
  /**
   * `full` is NOT a bigger pill. A pill is a shape for one or two words; a
   * pill holding a 70-character sentence either overflows its container or
   * wraps into a lozenge-shaped paragraph. Full density is therefore a
   * left-aligned notice strip: icon in the gutter, text in a column, full
   * width of its parent.
   */
  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    width: "100%",
    boxSizing: "border-box" as const,
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid transparent",
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: 1.45,
    textAlign: "left" as const,
  },
  // Pins the icon to the optical centre of the FIRST line rather than the
  // centre of the whole block, so it stays aligned when the text wraps.
  noticeIcon: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    height: "19px",
  },
  noticeBody: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "3px",
  },
  noticeCta: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontWeight: 600,
    fontSize: "12.5px",
  },
  clickable: {
    cursor: "pointer",
    background: "none",
    // `font: inherit` (the usual button reset) is a shorthand: it also resets
    // font-size and font-weight, and because this rule is emitted after
    // `.badge`/`.notice` it silently overrode their type scale. Only the
    // family actually needs resetting on a <button>.
    fontFamily: "inherit",
    "&:hover": { filter: "brightness(1.08)" },
    "&:focus-visible": {
      outline: `2px solid ${colors.primary}`,
      outlineOffset: "2px",
    },
  },
  label: { overflow: "hidden", textOverflow: "ellipsis" },
});

type Density = "compact" | "full";

type Props = {
  telematics?: TelematicsInfo | null;
  /** `compact` for table rows, `full` for the vehicle detail page. */
  density?: Density;
  /**
   * Show the upgrade prompt for `plan_excluded`. Off by default so a list of
   * 500 rows does not repeat the same upsell 500 times.
   */
  showPlanUpsell?: boolean;
};

/** Visual treatment per state. Icons are from the app's own Icon set. */
const STYLE_BY_STATUS: Record<
  TelematicsStatus,
  { icon: string; fg: string; bg: string; border: string }
> = {
  eligible: {
    icon: "map",
    fg: colors.success,
    bg: `${colors.success}1A`,
    border: `${colors.success}40`,
  },
  // Terminal negative, deliberately muted rather than red: nothing is broken
  // and there is no action to take, so it must not read as an error.
  not_eligible: {
    icon: "close",
    fg: colors.textSecondary,
    bg: "transparent",
    border: colors.borderLight,
  },
  pending: {
    icon: "reload",
    fg: colors.warning,
    bg: `${colors.warning}1A`,
    border: `${colors.warning}40`,
  },
  plan_excluded: {
    icon: "lock",
    fg: colors.primary,
    bg: `${colors.primary}14`,
    border: `${colors.primary}33`,
  },
  // Actionable data gap: the user can fix this by editing the vehicle.
  no_vin: {
    icon: "alertTriangle",
    fg: colors.warning,
    bg: `${colors.warning}1A`,
    border: `${colors.warning}40`,
  },
};

export const TelematicsBadge: FC<Props> = ({
  telematics,
  density = "compact",
  showPlanUpsell = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Older API responses have no `telematics` object. Render nothing rather
  // than guessing from the legacy boolean — a wrong badge is worse than none.
  if (!telematics?.status) return null;

  const { status, upgrade_unlocks: upgradeUnlocks } = telematics;

  if (status === "plan_excluded" && !showPlanUpsell) return null;

  const visual = STYLE_BY_STATUS[status];
  if (!visual) return null;

  // Two sentences per state: `long` is the meaning, `short` is what fits in a
  // table cell. The long form is always exposed via title/aria-label, so the
  // compact badge never costs the user information.
  const copy: Record<TelematicsStatus, { short: string; long: string }> = {
    eligible: {
      short: t("vehicle.telematics.eligible.short", "Géolocalisable"),
      long: t(
        "vehicle.telematics.eligible.long",
        "Véhicule éligible pour la géolocalisation et l'éco-conduite"
      ),
    },
    not_eligible: {
      short: t("vehicle.telematics.notEligible.short", "Non compatible"),
      long: t(
        "vehicle.telematics.notEligible.long",
        "Ce véhicule n'est pas compatible avec la géolocalisation et l'éco-conduite"
      ),
    },
    pending: {
      short: t("vehicle.telematics.pending.short", "Vérification en cours"),
      long: t(
        "vehicle.telematics.pending.long",
        "Éligibilité à la géolocalisation en cours de vérification auprès du fournisseur"
      ),
    },
    plan_excluded: {
      short: upgradeUnlocks
        ? t("vehicle.telematics.planExcluded.shortCompatible", "Compatible — non inclus")
        : t("vehicle.telematics.planExcluded.short", "Non inclus dans votre offre"),
      long: upgradeUnlocks
        ? t(
            "vehicle.telematics.planExcluded.longCompatible",
            "Ce véhicule est compatible avec la géolocalisation et l'éco-conduite. Ces fonctionnalités ne sont pas incluses dans votre offre actuelle."
          )
        : t(
            "vehicle.telematics.planExcluded.long",
            "La géolocalisation et l'éco-conduite ne sont pas incluses dans votre offre actuelle."
          ),
    },
    no_vin: {
      short: t("vehicle.telematics.noVin.short", "VIN manquant"),
      long: t(
        "vehicle.telematics.noVin.long",
        "Renseignez le VIN pour vérifier l'éligibilité à la géolocalisation et à l'éco-conduite"
      ),
    },
  };

  const { short, long } = copy[status];
  const isFull = density === "full";
  const isUpsell = status === "plan_excluded";
  const ctaLabel = t("billing.viewPlans", "Voir les offres");

  /**
   * Colour carries the STATE; it must not carry the text.
   *
   * The compact pill is two words on a tinted background, so tinted text is
   * fine. The notice is a full sentence, and 13px amber or grey-on-grey body
   * copy fails contrast and is tiring to read. So in full density the icon,
   * the border and the wash stay coloured while the sentence reverts to the
   * normal text colour — except `not_eligible`, which is deliberately quiet
   * because nothing is broken and there is nothing to do about it.
   */
  const noticeTextColor =
    status === "not_eligible" ? colors.textSecondary : colors.textPrimary;

  const style = {
    color: isFull ? noticeTextColor : visual.fg,
    backgroundColor: visual.bg,
    borderColor: visual.border,
  };

  const className = [
    isFull ? classes.notice : classes.badge,
    isUpsell ? classes.clickable : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = isFull ? (
    <>
      <span className={classes.noticeIcon}>
        <Icon name={visual.icon} width="16px" height="16px" color={visual.fg} />
      </span>
      <span className={classes.noticeBody}>
        <span>{long}</span>
        {/*
          The CTA is a visible line, not just a click target. The old strip was
          a button whose only affordance was the cursor: nothing on screen said
          it went anywhere, and the destination lived in the tooltip.
        */}
        {isUpsell && (
          <span className={classes.noticeCta} style={{ color: colors.primary }}>
            {ctaLabel}
            <span aria-hidden="true">&rarr;</span>
          </span>
        )}
      </span>
    </>
  ) : (
    <>
      <Icon name={visual.icon} width="14px" height="14px" color={visual.fg} />
      <span className={classes.label}>{short}</span>
    </>
  );

  // Only the upsell is interactive — it is the one state with somewhere to go.
  if (isUpsell) {
    return (
      <button
        type="button"
        className={className}
        style={style}
        // In full density the CTA is already on screen, so repeating it in the
        // tooltip is noise; in compact density the tooltip is the only place
        // the destination is announced.
        title={isFull ? long : `${long} — ${ctaLabel}`}
        aria-label={`${long} — ${ctaLabel}`}
        onClick={(e) => {
          e.stopPropagation();
          navigate("/billing");
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      className={className}
      style={style}
      // The full form already shows the whole sentence — a tooltip repeating it
      // verbatim is a redundant hover artefact.
      title={isFull ? undefined : long}
      aria-label={long}
      role="status"
    >
      {content}
    </span>
  );
};
